// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid2, IconButton, Slider, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseAvatar, BaseDatePicker, BaseLabel, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { ReportTypeEnum } from '~/utils/enum/common';
import { IHeaderCellType } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { formatCurrencyUSD, getFullName } from '~/utils/helper/common';
import { IForemanTaskReport, IQueryParamListExport } from '~/utils/interface/reports';
import { exportDataReport, getListForemanTaskCompletionReport } from '~/thunks/reports/reportsThunk';
import { BACK_TO_PREVIOUS_PAGE, DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './ReportsForemanTask.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IForemanTaskReport>[] => {
  return [
    {
      id: 'firstName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_foreman')}</Box>
        </Stack>
      ),
      render: (record: IForemanTaskReport) => (
        <Stack direction={'row'} alignItems={'center'} spacing={2}>
          <BaseAvatar
            dataAccount={{
              firstName: record?.firstName,
              lastName: record?.lastName,
            }}
            size={32}
            fontSize={14}
          />

          <Typography className={cx('textName')}>
            {getFullName({
              firstName: record?.firstName,
              lastName: record?.lastName,
            })}
          </Typography>
        </Stack>
      ),
    },
    // {
    //   id: 'total',
    //   label: (
    //     <Stack direction={'row'} alignItems={'center'} gap={2}>
    //       <Box>{t('report_table_total_revenue')}</Box>
    //     </Stack>
    //   ),
    //   render: (record: IForemanTaskReport) => {
    //     return formatCurrencyUSD({ value: record?.total, showDecimal: true });
    //   },
    // },
    {
      id: 'completedJob',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_completed_jobs')}</Box>
        </Stack>
      ),
      render: (record: IForemanTaskReport) => (
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <Typography className={cx('textQty')}>{record?.completedJob}</Typography>
          <Typography className={cx('variantName')}>{t('reports_foreman_task_completion_jobs')}</Typography>
        </Stack>
      ),
    },
    {
      id: 'scheduledJob',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_scheduled_jobs')}</Box>
        </Stack>
      ),
      render: (record: IForemanTaskReport) => (
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <Typography className={cx('textQty')}>{record?.scheduledJob}</Typography>
          <Typography className={cx('variantName')}>{t('reports_foreman_task_completion_jobs')}</Typography>
        </Stack>
      ),
    },
    {
      id: 'avgJobValue',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_avg_job_value')}</Box>
        </Stack>
      ),
      render: (record: IForemanTaskReport) => {
        return formatCurrencyUSD({ value: record?.avgJobValue, showDecimal: true });
      },
    },
    {
      id: 'completionRate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_completion_rate')}</Box>
        </Stack>
      ),
      render: (record: IForemanTaskReport) => (
        <Stack direction='row' alignItems='center' spacing={2} width={150}>
          <Slider
            className={cx('slider')}
            value={Number(record?.completionRate)}
            min={0}
            max={100}
            sx={{
              color: '#2D80FC',
              height: 8,
              '& .MuiSlider-thumb': { display: 'none' },
            }}
          />
          <Typography className={cx('textQty')}>{record?.completionRate}%</Typography>
        </Stack>
      ),
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_top_client')}</Box>
        </Stack>
      ),
      render: (record: IForemanTaskReport) => {
        return getFullName({
          firstName: record?.client?.[0]?.firstName,
          lastName: record?.client?.[0]?.lastName,
        });
      },
    },
  ];
};

const ReportsForemanTask = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [foremanTaskList, setForemanTaskList] = useState<IForemanTaskReport[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListForemanTask();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListForemanTask = () => {
    setIsLoading(true);

    dispatch(getListForemanTaskCompletionReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setForemanTaskList(responses);
        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListForemanTask();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.FOREMAN_TASK_COMPLETION,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };
  //#endregion Handle Function

  return (
    <Stack className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_foreman_task_completion_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_foreman_task_completion_desc_page')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            disabled={!foremanTaskList.length}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={foremanTaskList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ReportsForemanTask;
